// =============================================================================
// domain/commissions.ts — per-firm commission rates, and the fee they imply.
// -----------------------------------------------------------------------------
// PORTED FROM the old client's `perSideRateFor` / `commissionForTrade` /
// `applyCommissionToTrade` — semantics, not code (D3).
//
// ── 🚨 WHY THIS EXISTS AT ALL: THE DATABASE IS NOT THE SOURCE OF TRUTH ──
//
// A side-by-side comparison of the Trades tab showed the live app and the new
// client disagreeing on the SAME trade: commissions +$7.00 against $0.00, and a
// net P&L of -$132.00 against -$125.00. Neither was a rendering bug. The old
// client SELF-HEALS commissions on every load —
//
//     for (const t of (state.trades || [])) applyCommissionToTrade(t);
//
// — deriving the fee from the account firm's rate schedule and recomputing
// `netPnl`, because trades arrive fee-less from broker imports and from the
// realtime logger. The stored `fees` column is therefore STALE for any trade
// that has not been through an import-time or config-save backfill.
//
// A client that reads `fees` and `net_pnl` straight from the table shows numbers
// that are wrong by the commission on every affected trade — quietly, and in the
// FAVOURABLE direction, because omitted costs make results look better.
//
// ⚠ DIFFERENCE FROM THE OLD CLIENT, and it is deliberate: this returns a NEW row
// and never mutates or persists. The old client writes the healed values back
// through its save-diff. Phase 4 is read-only (Josh's call), so the derivation is
// applied at the read boundary and the database is left alone. When writes land,
// persisting is a separate decision — not a side effect of rendering a table.
// =============================================================================

/** One firm's rates, keyed by ROOT symbol: `{ ES: 2.10, MES: 0.62 }`. */
export interface CommissionSchedule {
  firm: string;
  rates: Readonly<Record<string, number>>;
}

/** Keyed by `firm_key` — the normalized (lowercased) firm name. */
export type CommissionSchedules = Readonly<Record<string, CommissionSchedule>>;

/** `"Apex"` and `"APEX"` must not be two schedules. Matches the DB's firm_key. */
export function normalizeFirm(s: string | null | undefined): string {
  return String(s ?? '').trim().toLowerCase();
}

/**
 * `ESM6` → `ES`. Rates are stored per root, and a stray expiry would miss.
 *
 * ⚠ Deliberately NOT `findContract` from `trade.ts`. That resolves against the
 * catalogue by longest prefix; this is a syntactic strip of a month+year suffix,
 * ported character for character, because the rate map is keyed by whatever the
 * user typed in the commission editor — which may not be a catalogued symbol.
 */
export function rootSymbol(sym: string | null | undefined): string {
  const s = String(sym ?? '').trim().toUpperCase();
  const m = s.match(/^([A-Z0-9]{1,3}?)[FGHJKMNQUVXZ]\d{1,2}$/);
  return m ? (m[1] as string) : s;
}

/** The per-SIDE rate for a firm + contract, or null when none is configured. */
export function perSideRate(
  firm: string | null | undefined,
  contract: string | null | undefined,
  schedules: CommissionSchedules,
): number | null {
  const key = normalizeFirm(firm);
  if (!key) return null;
  const sch = schedules[key];
  if (!sch?.rates) return null;
  const r = sch.rates[rootSymbol(contract)];
  return r != null && Number.isFinite(Number(r)) ? Number(r) : null;
}

/** What a trade needs for its fee to be derivable. */
export interface FeeTrade {
  contract: string | null;
  qty: number | null;
  grossPnl: number | null;
  fees: number | null;
  netPnl: number | null;
  /** `false` means the user typed the fee by hand — never overwrite it. */
  feesAuto: boolean | null;
}

/**
 * The ROUND-TURN fee: per-side rate × quantity × 2.
 *
 * ⚠ The `× 2` is the round turn — in and out. Dropping it halves every
 * commission, which is the kind of error that looks like a plausible number.
 */
export function commissionFor(
  t: FeeTrade,
  firm: string | null | undefined,
  schedules: CommissionSchedules,
): number | null {
  const rate = perSideRate(firm, t.contract, schedules);
  if (rate == null) return null;
  return rate * (Number(t.qty) || 0) * 2;
}

/**
 * The healed fee and net P&L, or the row unchanged.
 *
 * Returns a NEW object — see the header. Unchanged when:
 *   - the user overrode the fee by hand (`feesAuto === false`)
 *   - no rate is configured for this firm + instrument
 *
 * ⚠ `(grossPnl ?? 0) - fee`, ported literally. A trade with a rate but no gross
 * P&L therefore reports a net of exactly minus the commission, which is right:
 * the fee was genuinely paid. It differs from a null net, which means "no result
 * yet" — and only rows with a configured rate ever reach this line.
 */
export function applyCommission<T extends FeeTrade>(
  t: T,
  firm: string | null | undefined,
  schedules: CommissionSchedules,
): T {
  if (t.feesAuto === false) return t;
  const fee = commissionFor(t, firm, schedules);
  if (fee == null) return t;
  return { ...t, fees: fee, feesAuto: true, netPnl: (t.grossPnl ?? 0) - fee };
}
